import { useEffect, useRef, useState } from 'react'

import { getLocalStorageItem, setLocalStorageItem } from '~/libs/localStorage'

type Params<T> = {
  /** localStorage の key。画面ごとに一意にする。 */
  storageKey: string
  /**
   * 保存文字列 (未保存なら undefined) ↔ state の変換。通常は画面が
   * `defineFilterCodec` (`~/models/filterCodec`) で宣言した codec を渡す。
   */
  codec: {
    deserialize: (raw: string | undefined) => T
    serialize: (value: T) => string
  }
}

type Result<T> = {
  filters: T
  /**
   * 部分更新。 **updater が `prev` をそのまま返しても新しい参照を作る**
   * (= 「未操作か」 の判定に参照同一性を使うため、 更新したのに保存されない状態を
   * 作らない。 review 指摘 #6870: 契約と実装の不一致)。
   */
  updateFilters: (updater: (prev: T) => T) => void
}

/**
 * フィルタバーの選択状態を localStorage に保持し、画面遷移 (一覧 → 詳細 → 一覧) や
 * 再訪をまたいで復元する汎用 hook。
 *
 * spec `common/shared-components.md` の **AC-SC-031 [P0]** が「フィルタバーを持つ
 * 全画面において、フィルタの選択状態を localStorage で永続化し、他画面への遷移後・
 * 再訪時に前回の選択状態を復元する」と保存媒体まで規定する (タブ生存期間で消える
 * sessionStorage は「再訪時に復元」を満たさない)。
 *
 * component-local な `useState` のままだと route 遷移で page が unmount され、戻った
 * 時に全フィルタが初期値へリセットされる (デザイナー報告 #6684「結構 HF を誘発
 * しそう」)。
 *
 * **直列化・復元・既定値・バリデーションは `codec` (`~/models/filterCodec`) が SoT。**
 * ここは「読み書きのタイミング」だけを持つ。
 * 宣言の参照実装は `pages/ProcurementPlanList/models/persistedFilters.ts` (issue #6366)。
 *
 * ## 未操作の既定値は書かない
 *
 * 初回に既定値を書くと、`ServerClockGate` の待ち上限経路 (session RPC が MAX_WAIT_MS
 * 超過) で**端末時計由来の初期値が localStorage に載り**、offset 確定後の subtree
 * 作り直しでも保存値が優先されてサーバー時計で再導出できなくなる。作り直し時は本
 * hook が再初期化されるので、保存値が無ければ正しい時計で導出し直せる。
 *
 * run 回数ではなく**初期 state の参照同一性**で判定するのは React Strict Mode 対策。
 * effect の二重実行を run 数え上げで弾くと 2 回目で初期値を書いてしまう。
 * `updateFilters` は毎回新しいオブジェクトを作るため、未操作の間だけ
 * `filters === initialFilters` が成り立つ。
 */
export function usePersistedFilters<T>({
  storageKey,
  codec,
}: Params<T>): Result<T> {
  const [filters, setFilters] = useState<T>(() =>
    codec.deserialize(getLocalStorageItem(storageKey)),
  )

  const initialFiltersRef = useRef<T | null>(null)
  if (initialFiltersRef.current === null) {
    initialFiltersRef.current = filters
  }

  useEffect(() => {
    if (filters === initialFiltersRef.current) return
    setLocalStorageItem(storageKey, codec.serialize(filters))
    // `codec` は呼び出し側で毎レンダー新しい object になりうる (インライン定義) ため
    // 依存に入れない。 保存の契機は **filters が変わった時だけ** で、 codec の同一性は
    // 契機ではない。 storageKey は画面ごとに定数。
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 上記理由
  }, [filters, storageKey])

  return {
    filters,
    updateFilters: (updater) =>
      setFilters((prev) => {
        const next = updater(prev)
        // updater が `prev` を返した場合でも参照を変える。 同一参照のままだと
        // `filters === initialFilters` が成り立ち続け、 **操作したのに保存されない**。
        return next === prev ? { ...next } : next
      }),
  }
}
